import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { ArcIconButton, ArcStack, ArcText } from '@src/core/ui';
import { routesConfig } from '@src/core/routes';
import { useDisclosure } from '@src/core/hooks';
import { MissionIdParams } from '@src/core/entities';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { ChecklistList, ChecklistAgreement, ChecklistGamepadMessage, ChecklistActions } from './components';
import { buildChecklistItems, checklistItems, SIDEBAR_TITLE } from './constants';
import { safeDistanceLabel } from '@src/core/extensions';
import { useUnitType } from '@src/core/features/settings';

const initialCheckedItems = checklistItems.reduce((acc, item) => {
  acc[item.id] = false;
  return acc;
}, {} as Record<string, boolean>);

const ChecklistSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { missionId } = useParams<MissionIdParams>();
  const { data } = useGetOrderMissionQuery(missionId);

  // Render the checklist with the unit-aware clearance label; re-renders on a live
  // unit switch (UNITS Standard R1/R5). Static `checklistItems` (ids) drives state.
  const unitType = useUnitType();
  const items = React.useMemo(() => buildChecklistItems(safeDistanceLabel(unitType)), [unitType]);

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(initialCheckedItems);
  const { open: isAgree, toggle: handleAgreeToggle, show: setAgreeTrue, hide: setAgreeFalse } = useDisclosure(false);
  
  const droneSerialNumber = data?.drone?.serialNumber;

  const allChecked = Object.values(checkedItems).every(Boolean) && isAgree;

  // Sync agreement checkbox when all items are manually checked/unchecked
  useEffect(() => {
    const allItemsChecked = Object.values(checkedItems).every(Boolean);
    if (allItemsChecked && !isAgree) {
      setAgreeTrue();
    } else if (!allItemsChecked && isAgree) {
      setAgreeFalse();
    }
  }, [checkedItems, isAgree, setAgreeTrue, setAgreeFalse]);

  const handleItemCheck = (id: string) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAgreeToggleWithItems = () => {
    const newAgreeState = !isAgree;
    handleAgreeToggle();
    
    const allItemIds = checklistItems.map(item => item.id);
    const newCheckedItems = allItemIds.reduce((acc, id) => {
      acc[id] = newAgreeState;
      return acc;
    }, {} as Record<string, boolean>);
    
    setCheckedItems(newCheckedItems);
  };

  const onClose = () => navigate(routesConfig.orders, { replace: true });

  return (
    <ArcStack height='100%' minHeight={0} spacing={1} sx={{ overflow: 'hidden' }}>
      <ArcStack direction='row' justifyContent='space-between' alignItems='center' sx={{ pr: 0.5 }}>
        <ArcText.h4 fontSize={16} fontWeight={500} color='common.white'>
          CREATE MISSION
        </ArcText.h4>
        <ArcIconButton size='small' onClick={onClose} aria-label='Close' sx={{ p: 0 }}>
          <CloseIcon sx={{ color: 'common.white' }} />
        </ArcIconButton>
      </ArcStack>

      <ArcStack flex={1} minHeight={0} spacing={2} sx={{ overflowY: 'auto', pr: 0.5 }}>
        <ArcStack>
          <ArcText.h5 marginTop={1} flexShrink={0} fontSize={16} fontWeight={500} letterSpacing={0.4} color='common.white'>
            {SIDEBAR_TITLE}
          </ArcText.h5>
        </ArcStack>

        <ArcStack spacing={1}>
          <ChecklistList
            items={items}
            checkedItems={checkedItems}
            onItemToggle={handleItemCheck}
          />

          <ChecklistAgreement
            checked={isAgree}
            onToggle={handleAgreeToggleWithItems}
          />

        </ArcStack>
      </ArcStack>

      <ArcStack spacing={2} alignItems='center' flexShrink={0}>
        <ChecklistGamepadMessage serialNumber={droneSerialNumber} />
        <ChecklistActions allChecked={allChecked} />
      </ArcStack>
    </ArcStack>
  );
};

export default ChecklistSidebar;